import React, { useState } from 'react';
import { MessageBubbleProps, FileAttachment } from './types';
import { markdownToHtml } from '../utils/markdown';


const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  theme,
  isUser,
  onCopy,
  onDismiss,
  onSuggestionClick
}) => {
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);

  const formatTimestamp = (timestamp: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(timestamp);
  };

  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleCopy = async () => {
    const textToCopy = stripHtml(message.content);
    try {
      await navigator.clipboard.writeText(textToCopy);
      setShowCopyFeedback(true);
      setTimeout(() => setShowCopyFeedback(false), 2000);
      onCopy?.(textToCopy);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const renderAttachments = (attachments: FileAttachment[]) => {
    return (
      <div className="space-y-1.5 mb-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center gap-2 p-2 rounded-lg bg-white/10 border border-white/5"
          >
            <div className="w-5 h-5 bg-white/20 rounded flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="text-[11px] truncate">{attachment.name}</span>
          </div>
        ))}
      </div>
    );
  };

  // System Update notification
  if (message.meta?.isSystemUpdate) {
    return (
      <div className="mb-3 fade-up">
        <div className={`${theme.isDark ? 'bg-blue-900/20 border-blue-500/20' : 'bg-blue-50/80 border-blue-100'} border rounded-xl px-3.5 py-3 relative`}>
          {onDismiss && (
            <button
              onClick={() => onDismiss(message.id)}
              className={`absolute top-2 right-2 p-1 rounded-md ${theme.isDark ? 'hover:bg-blue-800 text-blue-500' : 'hover:bg-blue-100 text-blue-400'} transition-all`}
              aria-label="Dismiss"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
            <span className={`text-[9px] font-bold ${theme.isDark ? 'text-blue-400' : 'text-blue-600'} uppercase tracking-wider`}>Update</span>
          </div>
          <div
            className={`prose prose-xs max-w-none text-left ${theme.isDark ? 'text-blue-200' : 'text-blue-800'} prose-p:my-1 prose-p:text-xs prose-p:leading-relaxed prose-headings:text-[13px] prose-headings:font-semibold prose-headings:mt-2 prose-headings:mb-1 prose-ul:my-0.5 prose-li:my-0 prose-strong:font-semibold pr-5`}
            dangerouslySetInnerHTML={{ __html: markdownToHtml(message.content) }}
          />
          <span className={`text-[9px] ${theme.isDark ? 'text-blue-500' : 'text-blue-400'} mt-1.5 block`}>{formatTimestamp(message.timestamp)}</span>
        </div>
      </div>
    );
  }

  // User message
  if (isUser) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[85%]">
          <div className="bg-blue-600 text-white px-3.5 py-2.5 rounded-2xl rounded-br-md shadow-sm">
            {message.attachments && message.attachments.length > 0 && renderAttachments(message.attachments)}
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
          </div>
          <div className="flex justify-end mt-0.5 pr-0.5">
            <span className="text-[9px] text-slate-400">{formatTimestamp(message.timestamp)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex gap-2.5 mb-3">
      {/* AI Avatar */}
      <div className="w-7 h-7 bg-slate-800 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 space-y-1.5">
        <div className={`relative group ${theme.isDark ? 'bg-slate-700/60 text-slate-200' : 'bg-slate-50 text-slate-700'} px-3.5 py-2.5 rounded-2xl rounded-tl-md max-w-[90%]`}>
          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all ${theme.isDark ? 'hover:bg-slate-600 text-slate-500' : 'hover:bg-slate-200 text-slate-400'}`}
            aria-label="Copy"
            title="Copy"
          >
            {showCopyFeedback ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>

          {message.attachments && message.attachments.length > 0 && renderAttachments(message.attachments)}

          <div className="pr-5">
            {message.isStreaming ? (
              <span className="text-[13px] leading-relaxed whitespace-pre-wrap">
                {message.streamingContent || ''}
                <span className="inline-block w-1.5 h-3.5 bg-blue-500 ml-0.5 animate-pulse rounded-sm" />
              </span>
            ) : (
              <div
                className="prose prose-sm max-w-none text-left text-[13px] leading-relaxed prose-headings:text-slate-900 prose-headings:font-semibold prose-headings:text-[13px] prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0 prose-strong:font-semibold prose-strong:text-slate-800 prose-code:bg-slate-200 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-slate-700 prose-code:text-[11px]"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(message.content) }}
              />
            )}
          </div>

          {/* Execution metadata */}
          {message.meta?.confirmationExecuted && (
            <div className="mt-2 pt-1.5 border-t border-slate-200/60">
              <div className="flex flex-wrap items-center gap-2 text-[10px]">
                <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                  <span className="w-1 h-1 bg-emerald-500 rounded-full" />
                  Executed
                </span>
                {message.meta.executionTimeMs && (
                  <span className="text-slate-400">{message.meta.executionTimeMs}ms</span>
                )}
              </div>
            </div>
          )}

          {/*
            The "Data" footer badge was removed intentionally: the server now
            routes all capability results through the LLM formatter so the
            user gets a conversational answer, not a raw data dump. A badge
            labelling the response "Data" reinforces the old robotic feel.
          */}

          {message.workflowStep && (
            <div className="mt-2 pt-1.5 border-t border-slate-200/60">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${
                  message.workflowStep.status === 'completed' ? 'bg-emerald-500' :
                  message.workflowStep.status === 'in_progress' ? 'bg-blue-500' :
                  message.workflowStep.status === 'failed' ? 'bg-red-500' :
                  'bg-slate-400'
                }`} />
                <span className="text-[10px] font-medium text-slate-500">
                  Step {message.workflowStep.stepNumber}/{message.workflowStep.totalSteps}: {message.workflowStep.title}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Inline suggestions */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {message.suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-[11px] font-medium rounded-lg hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <span className="text-[9px] text-slate-400 pl-0.5">{formatTimestamp(message.timestamp)}</span>
      </div>
    </div>
  );
};

export default React.memo(MessageBubble);
